import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const HomeIcon = ({ active }: { active: boolean }) => (
  <svg className={`w-6 h-6 ${active ? 'text-green-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </svg>
);

const GroupIcon = ({ active }: { active: boolean }) => (
  <svg className={`w-6 h-6 ${active ? 'text-green-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const ScanIcon = ({ active }: { active: boolean }) => (
  <svg className={`w-7 h-7 ${active ? 'text-white' : 'text-white'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ActivityIcon = ({ active }: { active: boolean }) => (
  <svg className={`w-6 h-6 ${active ? 'text-green-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const ProfileIcon = ({ active }: { active: boolean }) => (
  <svg className={`w-6 h-6 ${active ? 'text-green-600' : 'text-slate-400'}`} fill={active ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

export const BottomNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-2 pb-[env(safe-area-inset-bottom)] z-50 md:hidden">
      <div className="flex justify-around items-center h-16">
        {/* Home */}
        <button
          onClick={() => navigate('/')}
          className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${isActive('/') && location.pathname === '/' ? 'text-green-600' : 'text-slate-500'}`}
        >
          <HomeIcon active={isActive('/') && location.pathname === '/'} />
          <span className="text-xs font-medium">Home</span>
        </button>

        {/* Groups */}
        <button
          onClick={() => navigate('/groups')}
          className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${isActive('/groups') ? 'text-green-600' : 'text-slate-500'}`}
        >
          <GroupIcon active={isActive('/groups')} />
          <span className="text-xs font-medium">Groups</span>
        </button>

        {/* Scan Bill - Primary Action */}
        <button
          onClick={() => navigate('/scan')}
          className="relative -mt-6"
        >
          <div className="w-14 h-14 bg-green-600 rounded-2xl shadow-lg shadow-green-500/40 flex items-center justify-center">
            <ScanIcon active={true} />
          </div>
        </button>

        {/* Activity */}
        <button
          onClick={() => navigate('/activity')}
          className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${isActive('/activity') ? 'text-green-600' : 'text-slate-500'}`}
        >
          <ActivityIcon active={isActive('/activity')} />
          <span className="text-xs font-medium">Activity</span>
        </button>

        {/* Profile */}
        <button
          onClick={() => navigate('/profile')}
          className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-colors ${isActive('/profile') ? 'text-green-600' : 'text-slate-500'}`}
        >
          <ProfileIcon active={isActive('/profile')} />
          <span className="text-xs font-medium">Profile</span>
        </button>
      </div>
    </nav>
  );
};

export default BottomNav;
